/**
 * Три уровня проверок до кнопки «Утвердить» — docs/phase-1/05-ui.md §4.2.
 *
 * Чистая функция без React и без DOM: её вызывает и панель решения (кнопки), и тесты, и в
 * фазе 2 её же переиспользует редактор. Ни одно правило здесь не написано заново — все
 * блокирующие проверки идут из `@smm/contracts` (checkDraftContent, canonicalize, buildTree,
 * лимиты). Причина в ПРАВИЛЕ 4.5 контракта: копия правил §4 в UI-компоненте стала бы
 * четвёртой рядом с генератором, подписью и предотправочным валидатором, и разошлась бы
 * первой — а расхождение здесь означает 400 на уже утверждённом посте.
 *
 * Что тут действительно СВОЁ и почему это не нарушает ПРАВИЛО 4.5: уровни 2 и 3 —
 * редакторские предупреждения, а не правила Telegram. Карточка превью, голая ссылка,
 * относительные даты и «сколько сущностей добавит сервер» ни на подпись, ни на отправку не
 * влияют; бэкенд их не считает намеренно (09-blockers-closed.md §9.1).
 */

import {
  buildTree,
  CanonicalizeError,
  canonicalize,
  checkDraftContent,
  codePointLen,
  type DraftContent,
  exceeds,
  type Issue,
  isAutoDetectedEntityType,
  type LinkPreviewOptions,
  lengthLimitFor,
  limitValue,
  linkPreviewFollowingText,
  type MessageEntity,
  NestingError,
  type PreviewEntity,
  REQUEST_MAX_UTF8_BYTES,
  richBlockIssues,
  SEND_MESSAGE_MAX_CODEPOINTS,
  SurrogateError,
  sortEntities,
  utf8Len,
  utf16Len,
  type WireMethodOptions,
  wireMethodOf,
} from '@smm/contracts';
import { expectedServerEntities, getFirstUrl } from '@smm/contracts/preview';
import type { UiIssue } from '../routes/contracts.ts';

/** Тумблер §3.5: «как выйдет в канал» против «только наша разметка». */
export type PreviewMode = 'expected' | 'canonical';
export type PreviewTheme = 'light' | 'dark';

/** Порог маршрутизации, он же потолок обычного сообщения. 4064 (units.ts). */
export const SEND_MESSAGE_THRESHOLD = limitValue(SEND_MESSAGE_MAX_CODEPOINTS);

/** Предел тела запроса Bot API — настоящий потолок расширенного сообщения (units.ts). */
export const REQUEST_BYTE_CAP = limitValue(REQUEST_MAX_UTF8_BYTES);

/**
 * Вход вопроса «каким способом уйдёт пост». `method` есть у ЗАМОРОЖЕННОГО содержания и у
 * содержания, чей способ уже выведен (`withDerivedTransport`, заморозка редактора); у сырого
 * набранного текста его нет, и тогда способ выводится.
 */
export type TransportInput = Pick<DraftContent, 'text' | 'media'> & {
  readonly method?: DraftContent['method'] | undefined;
  readonly document?: DraftContent['document'] | undefined;
};

/**
 * Какой метод уйдёт на провод. Своей формулы здесь нет и быть не может: правило живёт в
 * `wireMethodOf` (ПРАВИЛО 4.5, план 09 §4).
 *
 * С 19.09.2026 МЕТОД ВЫБИРАЕТСЯ ПРИ ЗАМОРОЗКЕ И ДАЛЬШЕ ЧИТАЕТСЯ (docs/release-2026-09-19.md
 * §9.3): с политикой бренда «все посты — расширенным сообщением» короткий пост без картинки
 * законно уходит rich, и вывод по (text, media) назвал бы его «обычным сообщением с карточкой»
 * — ровно ту ложь, от которой счётчик и плашка карточки существуют. Поэтому `method` есть —
 * ответ он. Нет — метод выводится тем же правилом, что выберет заморозка: картинка, документ,
 * длина и политика (`opts.richAlways`).
 *
 * Прежний довод «читать `method` нельзя — у набранной правки он от прошлой версии» остался в
 * силе и закрыт иначе: набранная правка приходит сюда через `withDerivedTransport`, который
 * метод ВЫВОДИТ заново, а не наследует.
 */
export function wireOf(c: TransportInput, opts: WireMethodOptions = {}): DraftContent['method'] {
  return c.method ?? wireMethodOf(c, opts);
}

/** Содержание, про которое можно сказать, будет ли у обычного сообщения карточка. */
export type PlainPhraseInput = TransportInput & {
  readonly entities?: readonly MessageEntity[] | undefined;
  readonly link_preview_options?: LinkPreviewOptions | null | undefined;
};

/**
 * Обычное сообщение словами — вместе с правдой о карточке. Карточка у `sendMessage` не
 * гарантирована: её отключают в версии, а бывает, что ссылки нет вовсе, — и «карточка снизу» по
 * одному методу была неправдой ровно там, где человек подписывает пост (снимок 19.09.2026).
 * Правило — `getFirstUrl`, то же, что у полосы карточки; у содержания без сущностей и опций
 * карточки (сырой набор) про карточку не сказано ничего.
 */
export function plainMessagePhrase(c: PlainPhraseInput): string {
  if (c.entities === undefined || c.link_preview_options === undefined) return 'обычное сообщение';
  const first = getFirstUrl({
    text: c.text,
    entities: c.entities,
    link_preview_options: c.link_preview_options,
    media: c.media,
    document: c.document ?? null,
    method: 'sendMessage',
  });
  return first.kind === 'card'
    ? 'обычное сообщение, карточка снизу'
    : 'обычное сообщение, без карточки';
}

/** Потолок длины в кодпоинтах — из lengthLimitFor: второй таблицы чисел в UI нет. */
export function limitFor(c: TransportInput): number {
  return limitValue(lengthLimitFor({ method: wireOf(c) }));
}

/**
 * Кодпоинтная ветка отдельно от байтовой: фраза «при лимите N кодпоинтов» имеет право
 * появиться, только когда упёрлись именно в кодпоинты. У расширенного сообщения этот предел
 * недостижим — `codePointLen(s) <= utf8Len(s)` для любой строки, значит RICH_MAX_CODEPOINTS
 * (32768) не сработает раньше REQUEST_MAX_UTF8_BYTES (32768) никогда, — и ветка остаётся
 * защитной. У обычного сообщения она и есть порог 4064.
 */
export function overCodepointLimit(c: TransportInput): boolean {
  return exceeds(lengthLimitFor({ method: wireOf(c) }), codePointLen(c.text));
}

/**
 * «Сохранять и одобрять нельзя» — оба потолка разом. Настоящий у расширенного сообщения
 * байтовый, и решает почти всегда он.
 */
export function overLimit(c: TransportInput): boolean {
  return exceeds(REQUEST_MAX_UTF8_BYTES, utf8Len(c.text)) || overCodepointLimit(c);
}

/**
 * Почему пост уходит расширенным сообщением — в порядке выбора метода при заморозке
 * (`freezeAuthored`): картинка, документ, длина, политика бренда. `null` — пост уходит обычным
 * сообщением. Та же развилка, что у `getFirstUrl`, но для мест, где карточки может и не быть
 * вовсе (счётчик, подпись под редактором): её причина всё равно одна из этих четырёх.
 */
export type RichReason = 'media' | 'document' | 'length' | 'policy';

export function richReasonOf(c: TransportInput, opts: WireMethodOptions = {}): RichReason | null {
  if (wireOf(c, opts) !== 'sendRichMessage') return null;
  if (c.media !== null) return 'media';
  if ((c.document ?? null) !== null) return 'document';
  return wireMethodOf({ text: c.text, media: null }) === 'sendRichMessage' ? 'length' : 'policy';
}

/**
 * Опции карточки для НАБРАННОГО текста. Не своя формула: это тот же
 * `linkPreviewFollowingText`, которым бэкенд собирает опции правки (`human-edit.ts` §6), —
 * второй вызов одной функции, а не второе правило.
 *
 * Опции правимой версии не наследуются намеренно (решение Ильи 2026-09-01): унаследованное
 * `is_disabled: true` от прошлого прогона показывало бы «карточки не будет» на тексте, где
 * она появится.
 */
export function cardOptionsOf(
  media: DraftContent['media'],
  entities: readonly MessageEntity[],
): LinkPreviewOptions | null {
  return media === null ? linkPreviewFollowingText(entities) : null;
}

/**
 * Сохранённая версия плюс набранный текст, с ПЕРЕСЧИТАННЫМ транспортом. Ровно то, что
 * сделает `prepare()` бэкенда после канонизации (план 09 §4).
 *
 * Оставить `method` и `link_preview_options` от сохранённой версии нельзя: правка,
 * перешагнувшая порог, показывала бы карточку, которой у неё уже не будет, а инвариант схемы
 * `(method === 'sendMessage') ⟺ (lpo !== null)` требует менять обе величины разом.
 *
 * Порог считается по НАБРАННОМУ тексту, а не по канонизированному: панель показывает
 * последствия набранного, и второй проход канонизации здесь означал бы, что превью и
 * счётчик расходятся с плашкой карточки, которая тоже смотрит на набранное (getFirstUrl).
 * Расхождение с заморозкой возможно ровно на хвостовых пробелах у самого порога — и там
 * права заморозка, а колонка исходника про срезанные хвосты уже говорит отдельно.
 *
 * `opts.richAlways` — политика бренда СЕЙЧАС (19.09.2026): несохранённая правка сохранится
 * по ней, и превью обязано показать ту форму, которую ей выберет заморозка. Документ версии
 * (PDF, §9.2) правкой текста не снимается и тоже делает пост rich.
 */
export function withDerivedTransport(
  base: DraftContent,
  text: string,
  entities: DraftContent['entities'],
  opts: WireMethodOptions = {},
): DraftContent {
  const method = wireMethodOf({ text, media: base.media, document: base.document ?? null }, opts);
  return {
    ...base,
    method,
    text,
    entities,
    link_preview_options: method === 'sendMessage' ? cardOptionsOf(base.media, entities) : null,
  };
}

/**
 * Сохранённая версия плюс картинка-КАНДИДАТ, с пересчитанным транспортом. Ради этой функции
 * весь трек и делается: приход картинки МЕНЯЕТ ТРАНСПОРТ, и «примете — карточки не будет»
 * обязано быть видно в самом превью, а не написано словами рядом.
 *
 * Своей формулы транспорта здесь нет и быть не может — тот же `wireMethodOf`, что у
 * заморозки (ПРАВИЛО 4.5). `LinkPreviewStrip` и `LengthMeter` переключатся сами: оба читают
 * `method`, который здесь выставлен заново (картинка делает пост rich при любой политике
 * бренда, поэтому `richAlways` сюда не нужен).
 *
 * Функции композируются: `withDerivedTransport(withProposedMedia(canonical, media), text,
 * entities)` даёт правильный ответ, потому что `withDerivedTransport` выводит метод из
 * `base.media`. Порядок обратный затёр бы медиа кандидата.
 *
 * ОГОВОРКА, которую надо назвать, а не обнаружить: если у сохранённой версии стоял ЯВНЫЙ
 * `link_preview_options.url`, `cardOptionsOf` его не восстановит — она считает карточку по
 * первой ссылке текста. На ветке «с картинкой» потери нет (там lpo обязан быть `null` по
 * инварианту схемы); потеря возможна только на предпросмотре СНЯТИЯ картинки. Сегодня явный
 * адрес в UI не выставляется ничем, но случай станет достижимым в день, когда выставится.
 */
export function withProposedMedia(base: DraftContent, media: DraftContent['media']): DraftContent {
  const method = wireMethodOf({ text: base.text, media, document: base.document ?? null });
  return {
    ...base,
    method,
    media,
    link_preview_options: method === 'sendMessage' ? cardOptionsOf(media, base.entities) : null,
  };
}

type EntitySource = Pick<DraftContent, 'text' | 'entities'>;

/**
 * Что именно подсвечивается в превью. Разница между двумя режимами и есть то, что добавит
 * Telegram сам (05-ui.md §3.5).
 *
 * Не бросает никогда: превью перерисовывается на каждый клик, и падение здесь оставило бы
 * человека без единственного, по чему он принимает решение. Сломанный вход виден как
 * блокирующая ошибка уровня 1 из validateDraft(), а не как пустой экран.
 */
export function entitiesForMode(source: EntitySource, mode: PreviewMode): readonly PreviewEntity[] {
  const own: readonly PreviewEntity[] = sortEntities(source.entities);
  if (mode === 'canonical') return own;
  try {
    return expectedServerEntities(source);
  } catch {
    return own;
  }
}

/** Сущности, которые проставит сам сервер: разница между режимами, показанная списком. */
export function autoAddedEntities(source: EntitySource): readonly PreviewEntity[] {
  try {
    return expectedServerEntities(source).filter((e) => isAutoDetectedEntityType(e.type));
  } catch {
    return [];
  }
}

/* ── маркеры относительного времени ── */

/**
 * Правило 1.6 контракта: между находкой темы и публикацией проходят дни, и «сегодня» в
 * посте к моменту выхода означает уже другой день. Список закрытый — расширять его наугад
 * значит поднимать шум на нормальном тексте.
 */
const RELATIVE_TIME_MARKERS: readonly string[] = [
  'сегодня',
  'вчера',
  'позавчера',
  'завтра',
  'послезавтра',
  'на этой неделе',
  'на прошлой неделе',
  'на следующей неделе',
];

/** Границы слова по буквам Unicode: `сегодняшний` маркером не является. */
function findRelativeMarkers(text: string): string[] {
  const lower = text.toLowerCase();
  const found: string[] = [];
  for (const marker of RELATIVE_TIME_MARKERS) {
    let from = 0;
    for (;;) {
      const at = lower.indexOf(marker, from);
      if (at < 0) break;
      const before = at === 0 ? '' : lower.charAt(at - 1);
      const after = lower.charAt(at + marker.length);
      const isLetter = (c: string): boolean => c !== '' && /\p{L}|\p{Nd}/u.test(c);
      if (!isLetter(before) && !isLetter(after)) {
        found.push(marker);
        break;
      }
      from = at + marker.length;
    }
  }
  return found;
}

/* ── сборка списка ── */

const SOURCE_LABEL: Readonly<Record<'explicit' | 'url_entity' | 'text_link', string>> = {
  explicit: 'ссылка, заданная в настройках карточки',
  url_entity: 'голая ссылка в тексте',
  text_link: 'ссылка, спрятанная в анкоре — анкор карточку не отменяет',
};

const ENTITY_TYPE_LABEL: Readonly<Record<string, string>> = {
  mention: 'упоминание',
  hashtag: 'хэштег',
  url: 'ссылка',
  email: 'адрес почты',
  cashtag: 'кэштег',
  bot_command: 'команда бота',
};

function issueToUi(i: Issue): UiIssue {
  return {
    level: i.severity === 'fatal' ? 'blocking' : 'warning',
    code: i.code,
    message: i.path === undefined ? i.message : `${i.message} (${i.path})`,
  };
}

/** Что экран знает сверх самой версии. */
export interface ValidateOptions {
  /**
   * Политика бренда «все посты — расширенным сообщением» СЕЙЧАС, если известна (§9.3).
   * `undefined` — неизвестна: предупреждения остаются в прежнем, осторожном виде.
   */
  readonly richAlways?: boolean | undefined;
}

/**
 * Полный список проверок для одной версии черновика.
 *
 * Порядок внутри уровня стабилен: список показывается человеку, и прыгающий порядок при
 * перерисовке читается как «что-то изменилось», хотя ничего не изменилось.
 */
export function validateDraft(
  canonical: DraftContent,
  opts: ValidateOptions = {},
): readonly UiIssue[] {
  const out: UiIssue[] = [];
  const seen = new Set<string>();
  const push = (issue: UiIssue): void => {
    const key = `${issue.level} ${issue.code} ${issue.message}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(issue);
  };

  const codepoints = codePointLen(canonical.text) as unknown as number;
  const utf16 = utf16Len(canonical.text) as unknown as number;
  const bytes = utf8Len(canonical.text) as unknown as number;
  const limit = limitFor(canonical);
  const rich = wireOf(canonical) === 'sendRichMessage';

  /* ── уровень 1: блокирующий ── */

  if (overCodepointLimit(canonical)) {
    push({
      level: 'blocking',
      code: 'text_too_long',
      message: `Превышен лимит длины: текст занимает ${codepoints} кодпоинтов при лимите ${limit}.`,
    });
  }

  if (exceeds(REQUEST_MAX_UTF8_BYTES, utf8Len(canonical.text))) {
    push({
      level: 'blocking',
      code: 'text_too_long_bytes',
      message: `Тело запроса — ${bytes} байт UTF-8 при пределе ${limitValue(REQUEST_MAX_UTF8_BYTES)}. Telegram ответит 400, и повторять запрос бесполезно.`,
    });
  }

  // Предусловия контракта: суррогатные границы, выход за текст, нулевая длина, запрещённые
  // типы (custom_emoji и автоопределяемые в ЯВНЫХ сущностях), text_link без http(s), язык pre.
  const pre = checkDraftContent(canonical);
  for (const i of pre) push(issueToUi(i));
  const hasPreFatal = pre.some((i) => i.severity === 'fatal');

  // buildTree зовём всегда: он ловит то, чего checkDraftContent не ловит по построению —
  // частичное пересечение сущностей. NestingError здесь не «ошибка рендера», а сигнал, что
  // сломана канонизация: после resplitEntities пересечений быть не может.
  // Режим «как выйдет в канал» строится из expectedServerEntities. Если он не строится,
  // превью показывает НАШУ разметку — то есть не то, что уйдёт в канал. Молчать об этом
  // нельзя: расхождение превью с публикацией и есть та единственная вещь, которую панель
  // обязана исключать. Поэтому провал — блокирующая ошибка, а не тихий откат.
  try {
    expectedServerEntities(canonical);
  } catch (e) {
    push({
      level: 'blocking',
      code: 'expected_entities_unavailable',
      message:
        'Не удалось вычислить, во что Telegram превратит разметку: превью показывает нашу ' +
        `форму, а не публикуемую. Одобрять нельзя. Причина: ${e instanceof Error ? e.message : String(e)}`,
    });
  }

  try {
    buildTree(canonical.text, canonical.entities);
  } catch (e) {
    if (e instanceof SurrogateError) {
      push({
        level: 'blocking',
        code: 'entity_boundary_in_surrogate_pair',
        message:
          'Граница сущности попала в середину суррогатной пары — Telegram ответит 400 уже после утверждения.',
      });
    } else if (e instanceof NestingError) {
      push({
        level: 'blocking',
        code: 'entity_nesting_invalid',
        message:
          'Сущности пересекаются частично, а не вложены. Каноническая форма такого не порождает — сломан конвейер канонизации.',
      });
    } else {
      throw e;
    }
  }

  // Цитата и код в расширенном сообщении уходят БЛОКАМИ и обязаны занимать целые строки
  // (docs/release-2026-09-19.md §9.3, `richBlockIssues`). Заморозка с 19.09.2026 отбивает
  // такую форму сама (`rich_block_misaligned`), но версия, замороженная rich-сообщением ДО
  // этого (картинка, длина), могла её пронести — и тогда отказ пришёл бы от подписи уже после
  // выбора слота. Здесь он виден до кнопки. Одна строка на все блоки: одинаковые коды в списке
  // читаются как повтор, а не как второй случай.
  if (canonical.method === 'sendRichMessage') {
    const misaligned = richBlockIssues(canonical.text, canonical.entities);
    const firstBlock = misaligned[0];
    if (firstBlock !== undefined) {
      push({
        level: 'blocking',
        code: 'rich_block_misaligned',
        message:
          `${firstBlock.message} Такую форму Telegram отобьёт после утверждения.` +
          (misaligned.length > 1 ? ` Таких мест: ${String(misaligned.length)}.` : ''),
      });
    }
  }

  // Черновик обязан приходить уже канонизированным (Б3.4). Расхождение — баг конвейера,
  // и увидеть его надо до подписи, а не после публикации.
  if (!hasPreFatal) {
    try {
      const recanonicalized = canonicalize(canonical);
      if (!sameContent(canonical, recanonicalized)) {
        push({
          level: 'blocking',
          code: 'draft_not_canonical',
          message:
            'Версия черновика не совпадает со своей канонической формой — Telegram сохранит не то, что показано. Обратитесь к инженеру.',
        });
      }
    } catch (e) {
      if (e instanceof CanonicalizeError) {
        push({ level: 'blocking', code: e.code, message: canonicalizeErrorMessage(e) });
      } else {
        throw e;
      }
    }
  }

  /* ── уровень 2: предупреждения ── */

  const auto = autoAddedEntities(canonical);

  const first = getFirstUrl(canonical);
  if (first.kind === 'card') {
    push({
      level: 'warning',
      code: 'link_preview_card',
      message: `Будет карточка превью ссылки — из ${first.host}. Её порождает ${SOURCE_LABEL[first.source]}: ${first.url}`,
    });
  }

  // ПРЕДОХРАНИТЕЛЬ §1.2ж: длинный пост без картинки уходит расширенным сообщением и теряет
  // карточку. Решение Ильи 06.09.2026 — случай допустим, но молчать о нём нельзя: глазом он
  // не виден, человек смотрит на текст без картинки и ждёт карточку снизу.
  //
  // При включённой политике бренда (`opts.richAlways`, §9.3) предупреждения нет: карточки нет
  // ни у одного поста бренда, человек выбрал это в «Настройках», а совет «уберите N, чтобы
  // вернуть карточку» стал бы ложью — сокращённый пост всё равно уйдёт расширенным.
  if (first.kind === 'none' && first.reason === 'rich_length' && opts.richAlways !== true) {
    push({
      level: 'warning',
      code: 'card_lost_to_length',
      message:
        `В тексте ${codepoints} кодпоинтов при пороге ${SEND_MESSAGE_THRESHOLD}, поэтому пост уйдёт ` +
        'расширенным сообщением, а у этого способа отправки параметра карточки превью нет вовсе. ' +
        `Чтобы вернуться к обычному сообщению с карточкой, нужно убрать ${codepoints - SEND_MESSAGE_THRESHOLD} кодпоинтов.`,
    });
  }

  if (first.kind === 'none' && first.reason === 'rich_media') {
    push({
      level: 'info',
      code: 'card_absent_with_media',
      message:
        'У поста есть картинка, поэтому он уходит расширенным сообщением: картинка первым ' +
        'блоком, карточки превью ссылки не будет.',
    });
  }

  if (first.kind === 'none' && first.reason === 'rich_document') {
    push({
      level: 'info',
      code: 'card_absent_with_document',
      message:
        'К посту приложен файл, поэтому он уходит расширенным сообщением: файл последним ' +
        'блоком, карточки превью ссылки не будет.',
    });
  }

  // `rich_policy` строки НЕ получает намеренно. Политика бренда включена по умолчанию, и
  // строка «карточки не будет: так решено в Настройках» стояла бы под каждым постом, приучая
  // не читать этот список. Причину называет плашка карточки под превью — одной строкой, там,
  // где карточку и ищут. Тот же довод у заморозки: `link_preview_dropped_for_rich` выдаётся,
  // только если карточку хотели.

  for (const e of auto) {
    if (e.type !== 'url') continue;
    push({
      level: 'warning',
      code: 'bare_url_in_text',
      message: `В тексте голая ссылка: ${canonical.text.slice(e.offset, e.offset + e.length)}. Telegram сам сделает её кликабельной.`,
    });
  }

  // Считается от ПОРОГА, а не от потолка: у расширенного сообщения потолок 32 768, и
  // предупреждение на 29 500 знаках не сработало бы никогда, промолчав там, где важно, —
  // на подходе к 4064, за которым карточка пропадает.
  if (!rich && codepoints > SEND_MESSAGE_THRESHOLD * 0.9) {
    push({
      level: 'warning',
      code: 'length_near_limit',
      message:
        `До порога ${SEND_MESSAGE_THRESHOLD} кодпоинтов осталось ${SEND_MESSAGE_THRESHOLD - codepoints}: ` +
        'выше него пост уйдёт расширенным сообщением и останется без карточки превью ссылки.',
    });
  }

  const markers = findRelativeMarkers(canonical.text);
  if (markers.length > 0) {
    push({
      level: 'warning',
      code: 'relative_time_marker',
      message: `Относительные даты в тексте: ${markers.join(', ')}. Между утверждением и слотом проходят дни — к публикации они будут означать другой день.`,
    });
  }

  /* ── уровень 3: информация ── */

  if (auto.length > 0) {
    const counts = new Map<string, number>();
    for (const e of sortEntities(auto)) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    const parts = [...counts.entries()].map(
      ([type, n]) => `${ENTITY_TYPE_LABEL[type] ?? type} — ${n}`,
    );
    push({
      level: 'info',
      code: 'auto_entities_added',
      message: `Telegram добавит сущностей: ${auto.length} (${parts.join(', ')}). В превью они показаны пунктиром.`,
    });
  }

  if (codepoints !== utf16) {
    push({
      level: 'info',
      code: 'non_bmp_present',
      message: `В тексте есть символы вне BMP: ${codepoints} кодпоинтов против ${utf16} единиц UTF-16. Лимит считает первое, смещения сущностей — второе.`,
    });
  }

  return out;
}

function canonicalizeErrorMessage(e: CanonicalizeError): string {
  const first = e.issues.find((i) => i.severity === 'fatal');
  return first === undefined
    ? `Черновик не приводится к канонической форме (${e.code}). Обратитесь к инженеру.`
    : `Черновик не приводится к канонической форме: ${first.message}`;
}

/**
 * Сравнение по значению, а не JSON.stringify: порядок ключей в объекте, пришедшем из JSON,
 * и в объекте, собранном canonicalize(), различается законно, и строковое сравнение
 * объявило бы неканоничным каждый второй нормальный черновик.
 */
function sameContent(a: DraftContent, b: DraftContent): boolean {
  if (a.method !== b.method || a.text !== b.text) return false;
  if (a.entities.length !== b.entities.length) return false;
  for (let i = 0; i < a.entities.length; i++) {
    const x = a.entities[i];
    const y = b.entities[i];
    if (x === undefined || y === undefined) return false;
    if (x.type !== y.type || x.offset !== y.offset || x.length !== y.length) return false;
    const xu = x.type === 'text_link' ? x.url : null;
    const yu = y.type === 'text_link' ? y.url : null;
    if (xu !== yu) return false;
    const xl = x.type === 'pre' ? (x.language ?? null) : null;
    const yl = y.type === 'pre' ? (y.language ?? null) : null;
    if (xl !== yl) return false;
  }
  const al = a.link_preview_options;
  const bl = b.link_preview_options;
  if ((al === null) !== (bl === null)) return false;
  if (al !== null && bl !== null) {
    if (
      al.is_disabled !== bl.is_disabled ||
      al.url !== bl.url ||
      al.prefer_small_media !== bl.prefer_small_media ||
      al.prefer_large_media !== bl.prefer_large_media ||
      al.show_above_text !== bl.show_above_text
    ) {
      return false;
    }
  }
  const am = a.media;
  const bm = b.media;
  if ((am === null) !== (bm === null)) return false;
  if (am !== null && bm !== null) {
    if (
      am.mime_type !== bm.mime_type ||
      am.sha256 !== bm.sha256 ||
      am.bytes !== bm.bytes ||
      am.show_caption_above_media !== bm.show_caption_above_media ||
      am.has_spoiler !== bm.has_spoiler
    ) {
      return false;
    }
  }
  // Документ — часть подписываемого (§9.2): версия, у которой канонизация потеряла бы или
  // подменила файл, обязана краснеть здесь так же, как с чужой картинкой.
  const ad = a.document ?? null;
  const bd = b.document ?? null;
  if ((ad === null) !== (bd === null)) return false;
  if (ad !== null && bd !== null) {
    if (
      ad.mime_type !== bd.mime_type ||
      ad.sha256 !== bd.sha256 ||
      ad.bytes !== bd.bytes ||
      ad.file_name !== bd.file_name
    ) {
      return false;
    }
  }
  return true;
}
